import { z } from "zod";
/** Notifications (design 3.9): mentions, assignments and subscriptions, derived from activity. */
const Id = z.number().int().positive();
export const NotificationMode = z.enum(["immediate", "digest", "off"]);
const TimeZone = z.string().min(1).max(64).refine((tz) => {
    try {
        new Intl.DateTimeFormat("en-GB", { timeZone: tz });
        return true;
    }
    catch {
        return false;
    }
}, "is not a known time zone");
export const NotificationSettings = z.object({
    mode: NotificationMode,
    /** local hour (0–23) the daily digest is sent */
    digestHour: z.number().int().min(0).max(23),
    timezone: z.string(),
});
export const UpdateNotificationSettingsInput = z
    .object({ mode: NotificationMode.optional(), digestHour: z.number().int().min(0).max(23).optional(), timezone: TimeZone.optional() })
    .strict();
export const NotificationReason = z.enum(["mention", "assigned", "subscribed"]);
export const Notification = z.object({
    id: Id,
    activityId: Id,
    objectId: Id,
    objectName: z.string(),
    reason: NotificationReason,
    /** the activity record: what happened, to what, by whom */
    entityKind: z.string(),
    entityId: z.number().int().nonnegative(),
    action: z.string(),
    fields: z.array(z.string()),
    actor: z.object({ kind: z.string(), id: Id.nullable(), name: z.string().nullable() }),
    at: z.coerce.date(),
    readAt: z.coerce.date().nullable(),
});
export const ListNotificationsQuery = z.object({
    unread: z.enum(["true", "false"]).optional(),
    /** notification id: return older ones */
    before: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const NotificationPage = z.object({ items: z.array(Notification), unread: z.number().int().nonnegative(), nextBefore: Id.nullable() });
export const MarkReadInput = z.union([z.object({ ids: z.array(Id).min(1).max(500) }).strict(), z.object({ all: z.literal(true) }).strict()]);
export const SubscriptionScope = z.enum(["object", "subtree"]);
/** A subscription set by hand: follow an object (or its subtree), or mute it. */
export const Subscription = z.object({
    objectId: Id,
    objectName: z.string(),
    scope: SubscriptionScope,
    muted: z.boolean(),
    createdAt: z.coerce.date(),
});
export const SetSubscriptionInput = z.object({ scope: SubscriptionScope.default("object"), muted: z.boolean().default(false) }).strict();
/** Whether the caller hears about changes to one object, and why. */
export const SubscriptionState = z.object({
    objectId: Id,
    subscribed: z.boolean(),
    /** why: a setting on this object, one inherited from an ancestor's subtree, or being its creator, owner or an assignee */
    via: z.enum(["object", "ancestor", "created", "owner", "assignee"]).nullable(),
    /** the hand-set row that decides it, if any */
    setting: z.object({ objectId: Id, scope: SubscriptionScope, muted: z.boolean() }).nullable(),
});
/** An email the worker sent or is trying to send (design 3.9). System admins see these. */
export const EmailDelivery = z.object({
    id: Id,
    kind: z.enum(["object", "digest", "test"]),
    userId: Id,
    to: z.string(),
    subject: z.string(),
    notificationCount: z.number().int().nonnegative(),
    attempts: z.number().int().nonnegative(),
    nextAttemptAt: z.coerce.date().nullable(),
    sentAt: z.coerce.date().nullable(),
    deadAt: z.coerce.date().nullable(),
    lastError: z.string().nullable(),
    createdAt: z.coerce.date(),
});
export const ListEmailDeliveriesQuery = z.object({
    /** given up, or failed at least once and still trying */
    failed: z.enum(["true", "false"]).optional(),
    before: z.coerce.number().int().positive().optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
//# sourceMappingURL=notifications.js.map